import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "@/App.css";
import Header from "@/components/Header";
import Directory from "@/components/Directory";
import MatrixOrgChart from "@/components/MatrixOrgChart";
import Insights from "@/components/Insights";
import ProfileModal from "@/components/ProfileModal";
import ProfileWizard, { ProfileCompletionBanner, profileCompletion } from "@/components/ProfileWizard";
import IdentifyDialog from "@/components/IdentifyDialog";
import { Toaster } from "@/components/ui/sonner";
import { useHashRoute } from "@/lib/useHashRoute";
import { IdentityProvider, useIdentity } from "@/lib/identity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppShell() {
  const [employees, setEmployees] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [identifyOpen, setIdentifyOpen] = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem("ezipper-wizard-dismissed") === "1"; } catch (err) { console.warn("Banner: citire localStorage eșuată", err); return false; }
  });
  const route = useHashRoute();
  const { me } = useIdentity();

  const meEmployee = useMemo(
    () => (me ? employees.find((e) => e.id === me.id) || null : null),
    [me, employees]
  );

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try { localStorage.setItem("ezipper-wizard-dismissed", "1"); } catch (err) { console.warn("Banner: salvare localStorage eșuată", err); }
  }, []);

  const fetchEmployees = useCallback(async () => {
    const r = await axios.get(`${API}/employees`);
    setEmployees(r.data);
  }, []);

  const fetchSkills = useCallback(async () => {
    const r = await axios.get(`${API}/skills`);
    setAllSkills(r.data);
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchEmployees(), fetchSkills()])
      .catch((e) => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [fetchEmployees, fetchSkills]);

  const handleEmployeeUpdated = useCallback((updated) => {
    setEmployees((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
    fetchSkills();
    setDataVersion((v) => v + 1);
  }, [fetchSkills]);

  const handleEmployeeCreated = useCallback((emp) => {
    setEmployees((prev) => [...prev, emp]);
    setDataVersion((v) => v + 1);
  }, []);

  const handleEmployeeDeleted = useCallback((id) => {
    setEmployees((prev) => prev.filter((e) => e.id !== id));
    fetchSkills();
    setDataVersion((v) => v + 1);
  }, [fetchSkills]);

  const handleSkillClick = useCallback((skill) => {
    setActiveSkill(skill);
    // Switch to directory view and close any modal so the filter is visible.
    if (route.view !== "directory") route.goToDirectory();
    if (route.profileId) route.closeProfile();
  }, [route]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 antialiased">
      <Header
        view={route.view}
        onGoDirectory={route.goToDirectory}
        onGoMatrix={route.goToMatrix}
        onGoInsights={route.goToInsights}
        totalCount={employees.length}
        onIdentify={() => setIdentifyOpen(true)}
        onOpenMyProfile={route.openProfile}
        profilePct={meEmployee ? profileCompletion(meEmployee).pct : null}
        onOpenWizard={() => setWizardOpen(true)}
      />

      <main data-testid="main-content">
        {meEmployee && !bannerDismissed && (
          <ProfileCompletionBanner
            employee={meEmployee}
            onOpen={() => setWizardOpen(true)}
            onDismiss={dismissBanner}
          />
        )}
        {error && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900/50">
              Eroare la încărcarea datelor: {error}
            </div>
          </div>
        )}

        {loading ? (
          <div className="max-w-7xl mx-auto px-4 py-20 text-center text-zinc-500 dark:text-zinc-400">
            Se încarcă directorul...
          </div>
        ) : route.view === "matrix" ? (
          <MatrixOrgChart employees={employees} onOpenProfile={route.openProfile} />
        ) : route.view === "insights" ? (
          <Insights
            onOpenProfile={route.openProfile}
            onSkillClick={handleSkillClick}
            refreshKey={dataVersion}
          />
        ) : (
          <Directory
            employees={employees}
            allSkills={allSkills}
            onOpenProfile={route.openProfile}
            activeSkill={activeSkill}
            onSetActiveSkill={setActiveSkill}
            onEmployeeCreated={handleEmployeeCreated}
          />
        )}
      </main>

      <ProfileModal
        employees={employees}
        employeeId={route.profileId}
        onClose={route.closeProfile}
        onOpenOther={route.openProfile}
        onEmployeeUpdated={handleEmployeeUpdated}
        onEmployeeDeleted={handleEmployeeDeleted}
        allSkills={allSkills}
        onSkillClick={handleSkillClick}
      />

      <IdentifyDialog open={identifyOpen} onOpenChange={setIdentifyOpen} />

      <ProfileWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        employee={meEmployee}
        allSkills={allSkills}
        onEmployeeUpdated={handleEmployeeUpdated}
      />

      <Toaster position="bottom-right" richColors closeButton />

      <footer className="border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <span>© {new Date().getFullYear()} eZipper — Director angajați</span>
          <span className="font-mono">v1.1</span>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <IdentityProvider>
      <AppShell />
    </IdentityProvider>
  );
}

export default App;
